/**
 * Dynamic user groups (docs/plans/element-sets-and-me.md, PR 7): a group whose
 * members are DERIVED rather than listed. `kind: 'dynamic'` and
 * `rule: { card_id, q }` — `q` is KQL over the items of `card_id` and its
 * descendants, and the members are the accounts LINKED to the matching items
 * (the card's identity element, `options.identity` on a User element). The
 * API evaluates the rule live on every request; nothing here materialises.
 *
 * Pure helpers — no React — shared by the Sharing tab's editor, the audience
 * surfaces and the tests.
 */
import { normalizeId } from './ids';
import { identityCardsOf, identityElementOf } from './userElements';

export { identityElementOf };

export const GROUP_KINDS = ['static', 'dynamic'];

/** 'static' | 'dynamic' — absent is static (every group written before PR 7). */
export const groupKind = group => (group?.kind === 'dynamic' ? 'dynamic' : 'static');

export const isDynamicGroup = group => groupKind(group) === 'dynamic';

/** The membership copy, one vocabulary for the editor and the row. */
export const MEMBERSHIP_OPTIONS = [
	{ value: 'static', label: 'People I add' },
	{ value: 'dynamic', label: 'Records matching a filter' },
];

export const LIVE_RULE_NOTE = 'Saved rules are evaluated live: people join and leave the group as their records change.';
export const NO_IDENTITY_CARD_NOTE = 'No card in this collection links records to accounts yet. Add a User element with the account link to a card first.';
export const SWITCH_TO_DYNAMIC_NOTE = 'Members you added will be removed once the rule is saved.';
export const SWITCH_TO_STATIC_NOTE = 'The rule will be removed. The group starts empty; add people to it below.';

/*
 * Read or write, never manage (owner ruling b73; keepr-api docs/SCHEMA.md §
 * Dynamic groups). Anyone who can write a record of a dynamic group's card can
 * link it to their own account and join the group, so the API refuses to give
 * one `manage` (400 `dynamic_group_role`) and refuses to switch a group that
 * manages to a rule. The picker never offers it; a grant stored before the
 * ruling shows read-only until the owner's downgrade turns it into Can edit.
 */
export const DYNAMIC_GROUP_ROLE_NOTE = 'A group built from records can view or edit, not manage. Give managers access one by one.';
export const DYNAMIC_GROUP_MANAGE_LEGACY_NOTE = "Groups built from records can't manage. This access will change to Can edit.";
export const DYNAMIC_GROUP_SWITCH_NOTE = 'This group has Manager access. A group built from records can view or edit, not manage: change its access to Can edit first.';
export const DYNAMIC_GROUP_ROLE_CODE = 'dynamic_group_role';

/** The access levels a group may be given: every one for a static group, all but manage for a dynamic one. */
export function roleOptionsForGroup(options, group) {
	return isDynamicGroup(group) ? (options || []).filter(option => option.value !== 'manage') : (options || []);
}

/**
 * The API's `dynamic_group_role` refusal in the product's words, or null for
 * any other error. The kind switch's refusal carries `grants` (how many
 * manager grants the group holds); a grant's does not.
 */
export function dynamicGroupRoleRefusal(error) {
	const data = error?.response?.data || {};
	if (data.code !== DYNAMIC_GROUP_ROLE_CODE) return null;
	return data.grants ? DYNAMIC_GROUP_SWITCH_NOTE : DYNAMIC_GROUP_ROLE_NOTE;
}

/**
 * The member cards a rule may name: those whose EFFECTIVE element list
 * (inherited, own and set elements — `getElementsForCard` is the cache's
 * resolver) carries an account link (`userElements.identityCardsOf`). A
 * descendant of a linked card qualifies through inheritance, exactly as the
 * API's write gate reads it.
 */
export function identityCardIds(memberCardIds, getElementsForCard) {
	const out = new Set();
	if (typeof getElementsForCard !== 'function') return out;
	const cards = [...(memberCardIds || [])].map(id => ({ _id: normalizeId(id) })).filter(card => card._id);
	identityCardsOf({ cards, getElementsForCard }).forEach(({ card }) => out.add(card._id));
	return out;
}

/** The rule as the wire carries it, or null when the group has none. */
export function ruleOf(group) {
	if (!isDynamicGroup(group) || !group?.rule) return null;
	const cardId = normalizeId(group.rule.card_id);
	const q = typeof group.rule.q === 'string' ? group.rule.q : '';
	return cardId ? { card_id: cardId, q } : null;
}

/**
 * Where a group write's 400 belongs. The API codes each refusal
 * (docs/SCHEMA.md § Dynamic groups): the rule ones name a `field`, so the
 * editor can put the sentence under the control that caused it; the rest
 * have no control to sit under and read as a toast.
 *
 *   { code, field: 'card_id' | 'q' | null, message }
 */
export function placeGroupError(error, fallback = 'Could not save the group.') {
	const data = error?.response?.data || {};
	const code = typeof data.code === 'string' ? data.code : null;
	const message = dynamicGroupRoleRefusal(error)
		|| (typeof data.message === 'string' && data.message ? data.message : fallback);
	let field = null;
	if (code === 'invalid_rule') field = data.field === 'card_id' || data.field === 'q' ? data.field : null;
	else if (code === 'rule_card_has_no_account_link') field = 'card_id';
	else if (code === 'query_me_unsupported') field = 'q';
	// A tag the rule names (T7: access mode — restricted tags only): the
	// refusal is about the rule's filter.
	else if (RULE_TAG_CODES.has(code)) field = 'q';
	return { code, field, message };
}

const RULE_TAG_CODES = new Set(['tag_not_restricted', 'unsupported_operator', 'unknown_tag', 'private_tag_not_allowed', 'ambiguous_tag', 'tags_not_yet_allowed']);
